import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Card,
  CardMedia,
  CardActions,
  Grid,
  LinearProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  PhotoCamera,
  Image as ImageIcon,
  Visibility
} from '@mui/icons-material';

interface UploadedImage {
  id: string;
  file: File;
  url: string;
  name: string;
  size: number;
}

interface ImageUploaderProps {
  label?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // in MB
  acceptedFormats?: string[];
  onImagesChange: (images: UploadedImage[]) => void;
  initialImages?: UploadedImage[];
  disabled?: boolean;
  showPreview?: boolean;
  uploadProgress?: number;
  error?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  label = 'Upload Images',
  multiple = true,
  maxFiles = 10,
  maxSize = 5, // 5MB default
  acceptedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  onImagesChange,
  initialImages = [],
  disabled = false,
  showPreview = true,
  uploadProgress,
  error
}) => {
  const [images, setImages] = useState<UploadedImage[]>(initialImages);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newImages: UploadedImage[] = [];
    const maxSizeBytes = maxSize * 1024 * 1024;

    Array.from(files).forEach((file) => {
      // Check file type
      if (!acceptedFormats.includes(file.type)) {
        console.warn(`File ${file.name} is not a supported format`);
        return;
      }

      // Check file size
      if (file.size > maxSizeBytes) {
        console.warn(`File ${file.name} is too large (max ${maxSize}MB)`);
        return;
      }

      // Check max files limit
      if (images.length + newImages.length >= maxFiles) {
        console.warn(`Maximum ${maxFiles} files allowed`);
        return;
      }

      const imageUrl = URL.createObjectURL(file);
      const newImage: UploadedImage = {
        id: `${Date.now()}-${Math.random()}`,
        file,
        url: imageUrl,
        name: file.name,
        size: file.size
      };

      newImages.push(newImage);
    });

    const updatedImages = multiple ? [...images, ...newImages] : newImages;
    setImages(updatedImages);
    onImagesChange(updatedImages);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled) return;
    
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (imageId: string) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    setImages(updatedImages);
    onImagesChange(updatedImages);

    // Revoke object URL to prevent memory leaks
    const imageToRemove = images.find(img => img.id === imageId);
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.url);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const openFileDialog = () => {
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {label}
      </Typography>

      {/* Upload Area */}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={openFileDialog}
        sx={{
          border: `2px dashed ${dragOver ? '#F8B133' : '#ddd'}`,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          backgroundColor: dragOver ? '#fff5e2' : 'transparent',
          transition: 'all 0.3s ease',
          '&:hover': disabled ? {} : {
            borderColor: '#F8B133',
            backgroundColor: '#fffaf0'
          }
        }}
      >
        <CloudUpload 
          sx={{ 
            fontSize: 48, 
            color: disabled ? 'text.disabled' : '#F8B133',
            mb: 2 
          }} 
        />
        <Typography variant="h6" gutterBottom>
          {dragOver ? 'Drop images here' : 'Drag & drop images here'}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          or click to browse files
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Supports: {acceptedFormats.map(format => format.split('/')[1]).join(', ')}
          <br />
          Max size: {maxSize}MB per file, Max files: {maxFiles}
        </Typography>

        <Button
          variant="outlined"
          startIcon={<PhotoCamera />}
          sx={{ 
            mt: 2,
            borderColor: '#F8B133',
            color: '#F8B133',
            '&:hover': {
              borderColor: '#f29100',
              backgroundColor: '#fff5e2'
            }
          }}
          disabled={disabled}
        >
          Choose Files
        </Button>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={acceptedFormats.join(',')}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {/* Upload Progress */}
      {uploadProgress !== undefined && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress}
            sx={{
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#F8B133'
              }
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Uploading... {Math.round(uploadProgress)}%
          </Typography>
        </Box>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Image Previews */}
      {showPreview && images.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Images ({images.length}/{maxFiles})
          </Typography>
          <Grid container spacing={2}>
            {images.map((image) => (
              <Grid item xs={12} sm={6} md={4} key={image.id}>
                <Card>
                  <CardMedia
                    component="img"
                    height="140"
                    image={image.url}
                    alt={image.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardActions sx={{ justifyContent: 'space-between', p: 1 }}>
                    <Box>
                      <Typography variant="caption" display="block" noWrap>
                        {image.name}
                      </Typography>
                      <Chip 
                        label={formatFileSize(image.size)} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => window.open(image.url, '_blank')}
                        title="Preview"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(image.id)}
                        color="error"
                        title="Remove"
                        disabled={disabled}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* File Count Info */}
      {images.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {images.length} of {maxFiles} files selected
          </Typography>
          {images.length > 0 && (
            <Button
              size="small"
              color="error"
              onClick={() => {
                images.forEach(img => URL.revokeObjectURL(img.url));
                setImages([]);
                onImagesChange([]);
              }}
              disabled={disabled}
            >
              Clear All
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ImageUploader;
